import type { IconName } from '@/constants/icons'

/**
 * Landing page content layer.
 *
 * All static copy for the landing page lives here. Organisms import from this file
 * rather than hardcoding strings. Copy is deliberately terse — the page demonstrates
 * rather than explains, so anything the tier chart can show is not written out.
 */

// -----------------------------------------------------------------------------
// SEO & Page Meta
// -----------------------------------------------------------------------------

/**
 * Short taglines that rotate on each page load.
 * Keep each under 25 chars so the full tab title ("Tagline | Avow")
 * stays under ~35 chars and renders without truncation on mobile Safari.
 */
export const LANDING_TAGLINES = [
  'Adjuster on standby',
  'Review before you report',
  'Prepared before disaster',
  'Your claim, pre-validated',
  'Protection with upside',
  'Ready before the storm',
]

// The share-card copy (LANDING_SEO, LANDING_OG_IMAGE_ALT) is in shared/constants/landingSeo.ts:
// the server writes the same tags into client-only routes.

// -----------------------------------------------------------------------------
// Hero
// -----------------------------------------------------------------------------

export const LANDING_HERO = {
  /** Rendered as two spans: the problem in ink, the answer in the second accent. */
  heading: {
    lead: 'Your deductible is sitting idle.',
    accent: 'It could be earning yield.',
  },
  /** One sentence, three benefits. The heading already names the problem. */
  subheading: 'Keep it in Avow. It pays out every two weeks, you can withdraw anytime, and a public adjuster reviews your claim before you file.',
  /** Sign-up lives at /apply; /membership only redirects non-members there. */
  cta: {
    join: { label: 'Become a member', href: '/apply' },
    member: { label: 'View membership', href: '/membership' },
  },
  /** The zero-deposit trial sits beside the CTA and links down to its own strip. */
  freeLink: {
    label: 'or start free for 90 days',
    href: '#trial',
  },
}

// -----------------------------------------------------------------------------
// Tier chart — what each paid tier earns, as bars
// -----------------------------------------------------------------------------

/**
 * The chart compares, it does not ask for a choice: the tier is picked on /apply.
 * Bar height is the yearly payout; the rows beneath show what each step up costs
 * (a bigger deposit) and narrows (the policy types it accepts).
 */
export const LANDING_TIERS = {
  labels: {
    bronze: 'Bronze',
    silver: 'Silver',
    gold: 'Gold',
  },
  heading: 'What each tier earns a year',
  /** Over each tier's deposit and its policies, any one of which qualifies. */
  specs: 'Deposit · any one of these policies',
  perYear: '/yr',
  /** Joined as "auto, renters, or homeowners": any one policy of these types qualifies. */
  policyLabels: {
    auto: 'auto',
    renters: 'renters',
    homeowners: 'homeowners',
  },
  /** Read out in place of the chart, one sentence per tier. */
  summary: '{tier}: {deposit} deposit, about {perYear} a year, needs {policies} insurance.',
  summaryLoading: '{tier}: {deposit} deposit, needs {policies} insurance.',
  /** The figure moves with Aave's rate and with how many members share each payout. */
  footnote: 'Live estimate from today’s vault rate and membership',
}

// -----------------------------------------------------------------------------
// Live metric rule
// -----------------------------------------------------------------------------

export const LANDING_METRIC_RULE = {
  label: 'Live',
  blockLabel: 'Block',
  /** Shown beside the Sponsor Deposits tile: the sponsor has made no commitment to stay. */
  sponsorNote: 'Sponsor deposits carry no commitment — they can be withdrawn at any time.',
}

// -----------------------------------------------------------------------------
// How it works — what joining takes, then what membership gives you
// -----------------------------------------------------------------------------

/**
 * Joining, as three steps in order. Plain words only: signing in needs no crypto know-how,
 * so the copy names Coinbase Wallet and nothing else — no gas, no signatures.
 */
export const LANDING_JOIN = {
  kicker: 'How to join',
  heading: 'Three steps, a few minutes',
  stepLabel: 'Step {n}',
  steps: [
    {
      id: 'sign-in',
      title: 'Sign in with Coinbase Wallet',
      body: 'Use your Coinbase Wallet, or create one as you go. Signing in is free.',
      icon: 'unlocked' as const satisfies IconName,
    },
    {
      id: 'proof',
      title: 'Show proof of insurance',
      body: 'Upload any document that shows your active renters, homeowners or auto coverage. A PDF or a photo works, and it’s checked in about a minute.',
      icon: 'document-protected' as const satisfies IconName,
      /** What counts: anything showing the carrier, policy number, insured name and dates. */
      documents: ['Declarations page', 'Auto ID card', 'Certificate of insurance', 'Full policy'],
    },
    {
      id: 'deposit',
      title: 'Meet the deposit',
      /** `{amounts}` is filled from the tier minimums, e.g. "$100, $500 or $1,000". */
      body: 'Deposit your tier’s amount in USDC: {amounts}. Short on USDC? You can buy it while you sign up.',
      icon: 'money' as const satisfies IconName,
    },
  ],
  /** Shown under the deposit step only while trial spots are open. */
  trialOffer: {
    label: 'Or start with $0 — {remaining} free trial {spots} left',
    spot: 'spot',
    spots: 'spots',
    href: '#trial',
  },
}

/**
 * What membership gives you, once joined. Benefits, not steps, so they carry no numbers.
 * Documenting is framed as what it buys you after a loss, not as a chore, and the adjuster
 * as someone on the member's side whom the evidence helps, not someone checking up on them.
 */
export const LANDING_MEMBERSHIP = {
  kicker: 'After you join',
  heading: 'What membership gives you',
  lead: 'Ready before a loss, backed through a claim, and paid the whole time.',
  items: [
    {
      id: 'document',
      title: 'Proof, before you need it',
      body: 'After a loss, the hardest part of a claim is proving what you had. A few guided photos of your home, rental or car today, and that proof is on file the day you need it.',
    },
    {
      id: 'adjuster',
      title: 'An adjuster in your corner',
      body: 'Talk your claim through with a public adjuster who works for you, not your insurer, before you file or after. Your photos give them the evidence to back you up.',
    },
  ],
  payouts: {
    title: 'Paid twice a month',
    body: 'Your deposit earns yield in a shared vault. On the 1st and 15th, that yield is split among members by tier, ready to collect from your dashboard.',
    /**
     * An adjuster-approved claim grants an exemption from the tier minimum for three months
     * (DEFAULT_EXEMPTION_MONTHS in server/domains/vendors/core/vendorDecisions.ts).
     */
    afterClaim: {
      label: 'After an approved claim',
      title: 'Take your deposit, keep your payouts',
      body: 'Your deposit normally has to stay at your tier’s amount for you to get paid. Once your adjuster approves a claim, you can withdraw all of it to cover your deductible and keep getting paid for three months. Top it back up by then to stay a member.',
    },
    /**
     * Labels for the sketch beside it: the shared vault before and after an approved claim.
     * It shows the shape of the rule, not real amounts.
     */
    chart: {
      normally: 'Normally',
      afterClaim: 'After an approved claim',
      vault: 'Shared vault',
      yours: 'Yours',
      paydays: '1st & 15th',
      payouts: 'Your payouts',
      deductible: 'Deductible',
      paid: 'Paid',
      stillPaid: 'Still paid',
    },
  },
}

// -----------------------------------------------------------------------------
// Trial strip
// -----------------------------------------------------------------------------

/** Trial members are paid too: they carry a payout weight and a $0 tier minimum. */
export const LANDING_TRIAL = {
  kicker: 'Free trial',
  heading: 'Try Avow free for 90 days',
  body: 'Everything you need to get Claim Ready, with nothing to deposit. Any renters, homeowners or auto policy qualifies.',
  perks: [
    'Guided property documentation',
    'Adjuster access once you\'re Claim Ready',
    'Earns payouts too',
  ],
  /** Centre of the 90-tick dial. */
  dial: {
    value: '$0',
    caption: 'deposit',
  },
  remaining: '{remaining} of {cap} spots left',
  /** "Claim" is kept for insurance claims, so the button says what it starts. */
  cta: 'Start my free trial',
  ctaHref: '/apply',
  /** membership.post claims the trial per policy number and carrier in trial_uses. */
  upgradeNote: 'One trial per policy. Move up to a paid tier anytime.',
  soldOut: 'All trial spots are taken. Bronze starts at $100 and accepts the same policies.',
  soldOutCta: 'Join as Bronze',
}

// -----------------------------------------------------------------------------
// FAQ — collapsed by default, answers under 25 words
// -----------------------------------------------------------------------------

/**
 * Every answer is something the code does today: no promise the app cannot keep. The
 * contracts are linked beside the questions so the money answers can be checked on-chain.
 */
export const LANDING_FAQ = {
  kicker: 'Questions',
  title: 'Straight answers',
  lead: 'Your deposit and every payout live on-chain, so you can check them yourself instead of taking our word for it.',
  verify: {
    heading: 'Check it yourself',
    vault: { label: 'Vault contract', detail: 'Holds every deposit' },
    payouts: { label: 'Payout contract', detail: 'Sends every payout' },
    note: 'Opens on BaseScan',
  },
  groups: [
    {
      id: 'money',
      title: 'Your money',
      items: [
        {
          q: 'Can I take my deposit out?',
          a: 'Yes, anytime, with no lockup or fee. Keep your tier’s amount in to stay a member. If your membership lapses, rejoining restarts the wait before your first payout.',
        },
        {
          q: 'Where is my deposit held?',
          a: 'In Avow’s vault on Base, which lends it on Aave to earn yield. Anyone can see what the vault holds on-chain. Like any on-chain protocol, it carries smart-contract risk.',
        },
        {
          q: 'How are payouts split?',
          a: 'On the 1st and 15th, the vault’s yield is shared among members by tier, so higher tiers earn more. Depositing more than your tier’s amount doesn’t earn extra.',
        },
      ],
    },
    {
      id: 'claims',
      title: 'Claims',
      items: [
        {
          q: 'What is a public adjuster?',
          a: 'A claims professional who works for you, not your insurer. Yours helps with a claim before you file or after, using the evidence you’ve documented.',
        },
        {
          q: 'What happens when my claim is approved?',
          a: 'You can withdraw your deposit to cover your deductible and keep getting paid for three months. Top it back up before then to keep your membership.',
        },
        {
          q: 'What do I have to document?',
          a: 'Only what your policy type calls for, like major systems, serial numbers and condition photos. A checklist walks you through it, and each upload is checked automatically.',
        },
      ],
    },
    {
      id: 'start',
      title: 'Getting started',
      items: [
        {
          q: 'Do I need to know crypto?',
          a: 'No. You sign in with Coinbase Wallet, which you can create as you join. Deposits are in USDC, a dollar-backed digital currency you can buy during sign-up.',
        },
        {
          q: 'When do I start earning?',
          /** calculateDistributionEligibleDate: every join or rejoin sits out one full cycle. */
          a: 'After one full payout cycle, whether you’re new or rejoining. Join between the 1st and 14th and your first payout is on the 1st of next month; join from the 15th and it’s the 15th of next month.',
        },
      ],
    },
  ],
}
